import { create } from "zustand";
import type { Coordinate } from "@/entities/tracking";
import { env } from "@/shared/constants/env";
import * as Location from "@/shared/platform/location";

// Autorisation de localisation et dernière position connue — SOURCE UNIQUE.
//
// ## Le problème que ce store résout
// Sept endroits demandaient la permission chacun de leur côté (accueil, carte,
// suivi, matching, zone agent, traqueur agent, sélecteur d'adresse). Sur web,
// `requestForegroundPermissionsAsync()` ne se contente pas de demander : il
// DÉCLENCHE une vraie localisation (cf. shared/platform/location). Sept appels,
// c'est donc sept allumages du GPS, sept attentes de plusieurs secondes — et,
// tant que l'utilisateur n'avait pas encore répondu, plusieurs invites du
// navigateur qui se chevauchaient.
//
// Ici, tout passe par un seul point : l'autorisation est demandée UNE fois, la
// position est mise en cache, et chaque appelant réutilise ce qui existe déjà.
//
// ## Ce qui change par rapport au natif
// Un refus est MÉMORISÉ PAR LE NAVIGATEUR pour l'origine. On ne peut pas
// re-solliciter l'utilisateur : `getCurrentPosition` échoue immédiatement, sans
// rien afficher. La seule issue passe par les réglages du site — d'où le modal
// explicatif, qui donne la marche à suivre au lieu de laisser un bouton sans
// effet.

/**
 * - `unknown`     : pas encore consulté (état de départ)
 * - `prompt`      : jamais demandé, l'invite navigateur reste possible
 * - `granted`     : accordée
 * - `denied`      : refusée — définitif tant que l'utilisateur ne change pas
 *                   les réglages du site
 * - `unsupported` : pas de `navigator.geolocation` (navigateur ancien, ou
 *                   origine non sécurisée : la géolocalisation exige HTTPS)
 */
export type GeoPermission = "unknown" | "prompt" | "granted" | "denied" | "unsupported";

type GeolocationState = {
  permission: GeoPermission;
  /**
   * L'autorisation a DEJA ete accordee au moins une fois sur cet appareil.
   *
   * Firefox (et Safari) n'enregistrent la permission que si l'utilisateur coche
   * « Retenir ce choix ». Sans cela, `permissions.query` repasse a `prompt` des
   * la requete suivante — et un ecran de blocage qui ne regarderait que
   * `permission` reapparaitrait EN PLEINE UTILISATION, alors que l'utilisateur
   * vient d'accepter. On garde donc trace du consentement, cote app.
   */
  hasEverGranted: boolean;
  /** Dernière position connue, quelle qu'en soit la source. */
  position: Coordinate | null;
  /** Horodatage de `position` (ms), pour juger de sa fraîcheur. */
  positionAt: number | null;
  /** Une demande est en cours — évite d'empiler les invites. */
  isRequesting: boolean;
  /**
   * Le modal expliquant comment rouvrir l'autorisation est-il affiché ?
   *
   * Deux moments l'ouvrent, et deux seulement :
   *
   *   1. À CHAQUE CONNEXION, si la localisation n'est pas active et que
   *      l'invite native ne peut plus rien donner (refus déjà mémorisé par le
   *      navigateur) — cf. `LocationGate` ;
   *   2. sur une ACTION qui exige de savoir où se trouve l'utilisateur, via
   *      `requirePosition`.
   *
   * Jamais sur un simple affichage : une carte qui se recentre ailleurs ou un
   * libellé de quartier vide se dégradent en silence. C'est ce qui distingue
   * une relance utile d'un modal qui surgit à chaque écran.
   */
  isPermissionDialogOpen: boolean;

  /** Lit l'état sans rien demander, et suit ses changements. */
  init: () => void;
  /** Demande l'autorisation. Résout `true` si elle est accordée. */
  request: () => Promise<boolean>;
  /**
   * Position utilisable, en réutilisant le cache tant qu'il est assez frais.
   * `null` si l'autorisation manque ou si aucun capteur ne répond.
   */
  ensurePosition: (maxAgeMs?: number) => Promise<Coordinate | null>;
  /** Alimente le cache depuis une autre source (traqueur agent, carte…). */
  reportPosition: (coord: Coordinate) => void;
  /**
   * Position pour une action qui EXIGE de savoir où se trouve l'utilisateur.
   *
   * Tente d'obtenir un point ; si l'autorisation manque, ouvre le modal
   * explicatif et rend `null`. C'est le seul endroit qui affiche ce modal —
   * ailleurs, l'absence de position se dégrade en silence.
   */
  requirePosition: (maxAgeMs?: number) => Promise<Coordinate | null>;
  /**
   * Ouvre le modal explicatif SANS tenter d'obtenir une position.
   *
   * Sert quand on sait déjà que l'invite native est impossible — permission
   * `denied` ou `unsupported` : le navigateur mémorise le refus par origine et
   * `getCurrentPosition` échouerait instantanément, sans rien afficher.
   * Passer par `requirePosition` dans ce cas allumerait le capteur pour rien.
   */
  openPermissionDialog: () => void;
  closePermissionDialog: () => void;
};

// Consentement mémorisé côté app, pour survivre aux rechargements. Un
// navigateur qui ne retient pas la permission ne doit pas nous faire rejouer
// l'écran de blocage à chaque visite.
const GRANTED_FLAG_KEY = "alomain.geo.granted";

function readGrantedFlag(): boolean {
  try {
    return localStorage.getItem(GRANTED_FLAG_KEY) === "1";
  } catch {
    return false; // stockage bloqué (navigation privée) : on repart de zéro
  }
}

function rememberGranted(): void {
  try {
    localStorage.setItem(GRANTED_FLAG_KEY, "1");
  } catch {
    /* best effort */
  }
}

/** Une position de moins de deux minutes est réutilisée telle quelle. */
const DEFAULT_MAX_AGE_MS = 120_000;

/**
 * Demande en vol, partagée.
 *
 * Deux écrans qui se montent en même temps appelaient `request()` chacun, et le
 * navigateur affichait deux invites successives. Ils attendent désormais la
 * même promesse.
 */
let inFlightRequest: Promise<boolean> | null = null;
let inFlightPosition: Promise<Coordinate | null> | null = null;
let permissionWatcherStarted = false;

export const useGeolocationStore = create<GeolocationState>((set, get) => ({
  permission: "unknown",
  hasEverGranted: readGrantedFlag(),
  position: null,
  positionAt: null,
  isRequesting: false,
  isPermissionDialogOpen: false,

  init: () => {
    if (permissionWatcherStarted) return;
    permissionWatcherStarted = true;

    if (typeof navigator === "undefined" || !navigator.geolocation) {
      set({ permission: "unsupported" });
      return;
    }

    void (async () => {
      const res = await Location.getForegroundPermissionsAsync();
      // `undetermined` recouvre deux cas très différents : « jamais demandé »
      // et « ce navigateur ne sait pas répondre » (Safari n'implémente pas
      // l'API Permissions pour la géolocalisation). Les deux se traitent
      // pareil — il faut tenter la demande pour savoir.
      const initial: GeoPermission =
        res.status === "granted" ? "granted" : res.status === "denied" ? "denied" : "prompt";
      if (initial === "granted") rememberGranted();
      set({ permission: initial, ...(initial === "granted" ? { hasEverGranted: true } : {}) });

      // Suivi des changements : l'utilisateur peut autoriser depuis les
      // réglages du site, onglet ouvert. Sans cette écoute, il resterait
      // bloqué devant l'écran de blocage jusqu'à un rechargement manuel.
      try {
        const status = await navigator.permissions?.query({ name: "geolocation" });
        if (!status) return;
        status.addEventListener("change", () => {
          const next: GeoPermission =
            status.state === "granted"
              ? "granted"
              : status.state === "denied"
                ? "denied"
                : "prompt";
          set({ permission: next, ...(next === "granted" ? { hasEverGranted: true } : {}) });
          if (next === "granted") {
            rememberGranted();
            // Autorisation fraîchement accordée : on récupère une position tout
            // de suite, pour que l'app démarre déjà localisée.
            void get().ensurePosition(0);
          }
        });
      } catch {
        // API Permissions absente : on se contentera de l'état lu plus haut.
      }
    })();
  },

  request: async () => {
    if (get().permission === "granted") return true;
    if (inFlightRequest) return inFlightRequest;

    set({ isRequesting: true });

    inFlightRequest = (async () => {
      try {
        const res = await Location.requestForegroundPermissionsAsync();

        if (res.granted) {
          // La demande a nécessairement produit une position (cf. le wrapper) :
          // on la récupère sans rallumer le capteur.
          const last = await Location.getLastKnownPositionAsync();
          rememberGranted();
          set({
            permission: "granted",
            hasEverGranted: true,
            ...(last
              ? {
                  position: {
                    latitude: last.coords.latitude,
                    longitude: last.coords.longitude,
                  },
                  positionAt: Date.now(),
                }
              : {}),
          });
          return true;
        }

        // `undetermined` après une demande = échec technique (timeout, capteur
        // muet), PAS un refus. Le distinguer évite de bloquer l'app derrière un
        // écran de refus alors que l'utilisateur n'a rien refusé.
        set({ permission: res.status === "denied" ? "denied" : "prompt" });
        return false;
      } finally {
        set({ isRequesting: false });
        inFlightRequest = null;
      }
    })();

    return inFlightRequest;
  },

  ensurePosition: async (maxAgeMs = DEFAULT_MAX_AGE_MS) => {
    const { position, positionAt, permission } = get();

    // 1. Cache assez frais → réponse immédiate, aucun capteur sollicité.
    if (position && positionAt != null && Date.now() - positionAt <= maxAgeMs) {
      return position;
    }

    // 2. Refus connu → inutile d'insister, l'appel échouerait en silence.
    if (permission === "denied" || permission === "unsupported") return null;

    // 3. Autorisation pas encore accordée → la demander (dédupliquée).
    if (permission !== "granted") {
      const ok = await get().request();
      if (!ok) return null;
    }

    if (inFlightPosition) return inFlightPosition;

    inFlightPosition = (async () => {
      try {
        const pos = await Location.getCurrentPositionAsync({
          accuracy: Location.Accuracy.Balanced,
        });
        const coord = { latitude: pos.coords.latitude, longitude: pos.coords.longitude };
        set({ position: coord, positionAt: Date.now() });
        return coord;
      } catch (e) {
        if (env.isDev) console.warn("[geolocation] position indisponible:", e);
        // Une position périmée vaut mieux que rien : le client la corrigera sur
        // la carte, alors qu'un champ vide l'oblige à tout ressaisir.
        return get().position;
      } finally {
        inFlightPosition = null;
      }
    })();

    return inFlightPosition;
  },

  reportPosition: (coord) => set({ position: coord, positionAt: Date.now() }),

  requirePosition: async (maxAgeMs) => {
    const coord = await get().ensurePosition(maxAgeMs);
    if (coord) return coord;

    // Pas de position ET pas d'autorisation : c'est le moment d'expliquer.
    // On ne le fait qu'ici, sur une action qui en dépend vraiment.
    set({ isPermissionDialogOpen: true });
    return null;
  },

  openPermissionDialog: () => set({ isPermissionDialogOpen: true }),

  closePermissionDialog: () => set({ isPermissionDialogOpen: false }),
}));

/** Accès hors composant (services, fonctions utilitaires). */
export const geolocation = {
  ensurePosition: (maxAgeMs?: number) => useGeolocationStore.getState().ensurePosition(maxAgeMs),
  requirePosition: (maxAgeMs?: number) => useGeolocationStore.getState().requirePosition(maxAgeMs),
  reportPosition: (coord: Coordinate) => useGeolocationStore.getState().reportPosition(coord),
  getCached: () => useGeolocationStore.getState().position,
};
